import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  InputAdornment,
  IconButton,
  CircularProgress,
  Fade,
  Stack,
  Paper,
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  VpnKey as KeyIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { apiUrl } from '../utils/api';
import { validatePassword } from '../utils/passwordValidator';
import PasswordPolicyChecklist from '../components/PasswordPolicyChecklist';

interface ForcePasswordChangeProps {
  user: any;
  onPasswordChanged: (updatedUser: any) => void;
  onLogout: () => void;
}

/**
 * Full-page screen shown after login + MFA when the user has been flagged
 * to change their password (new admin-created accounts and admin-driven
 * password resets). The user cannot reach the rest of the app until they
 * pick a new password that satisfies the policy and isn't in their last
 * 5 password history.
 */
const ForcePasswordChange: React.FC<ForcePasswordChangeProps> = ({
  user,
  onPasswordChanged,
  onLogout,
}) => {
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const { isValid: isPasswordValid } = validatePassword(newPw);
  const passwordsMatch = newPw === confirmPw;
  const canSubmit =
    isPasswordValid && passwordsMatch && newPw.length > 0 && !loading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setLoading(true);

    try {
      const response = await fetch(apiUrl('/api/users/me/force-change-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          new_password: newPw,
          confirm_password: confirmPw,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Password change failed. Please try again.');
        return;
      }

      onPasswordChanged(data.user);
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        height: '100%',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          width: '600px',
          height: '600px',
          background: 'linear-gradient(135deg, #2f4ae1 0%, #12b59f 100%)',
          borderRadius: '50%',
          top: '-300px',
          right: '-200px',
          opacity: 0.1,
          filter: 'blur(80px)',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          width: '400px',
          height: '400px',
          background: 'linear-gradient(135deg, #12b59f 0%, #2f4ae1 100%)',
          borderRadius: '50%',
          bottom: '-150px',
          left: '-100px',
          opacity: 0.1,
          filter: 'blur(60px)',
        },
      }}
    >
      {/* Logout button at top-right so the user can bail out if something
          goes wrong instead of being permanently stuck on this screen. */}
      <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 2 }}>
        <Button
          variant="text"
          color="inherit"
          startIcon={<LogoutIcon />}
          onClick={onLogout}
          sx={{ textTransform: 'none', color: '#1f2937' }}
        >
          Logout
        </Button>
      </Box>

      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Fade in timeout={800}>
          <Box>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Paper
                elevation={0}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 80,
                  height: 80,
                  borderRadius: '20px',
                  background: 'linear-gradient(135deg, #2f4ae1 0%, #12b59f 100%)',
                  mb: 2,
                  boxShadow: '0 8px 32px rgba(47, 74, 225, 0.3)',
                }}
              >
                <KeyIcon sx={{ fontSize: 44, color: 'white' }} />
              </Paper>
              <Typography
                variant="h4"
                fontWeight={700}
                gutterBottom
                sx={{ color: '#1f2937', mb: 1 }}
              >
                Set a New Password
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user?.user_email
                  ? `Welcome ${user.user_email}. `
                  : ''}
                You must change your password before continuing.
              </Typography>
            </Box>

            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                background: 'white',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(0, 0, 0, 0.05)',
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <form onSubmit={handleSubmit}>
                  <Stack spacing={2.5}>
                    {error && (
                      <Fade in>
                        <Alert
                          severity="error"
                          onClose={() => setError(null)}
                          sx={{ borderRadius: 2 }}
                        >
                          {error}
                        </Alert>
                      </Fade>
                    )}

                    <TextField
                      fullWidth
                      label="New Password"
                      type={showPw ? 'text' : 'password'}
                      value={newPw}
                      onChange={(e) => setNewPw(e.target.value)}
                      autoFocus
                      disabled={loading}
                      error={newPw.length > 0 && !isPasswordValid}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon sx={{ color: '#2f4ae1' }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPw(!showPw)}
                              edge="end"
                              disabled={loading}
                              size="small"
                            >
                              {showPw ? (
                                <VisibilityOff fontSize="small" />
                              ) : (
                                <Visibility fontSize="small" />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    <PasswordPolicyChecklist password={newPw} />

                    <TextField
                      fullWidth
                      label="Confirm New Password"
                      type={showPw ? 'text' : 'password'}
                      value={confirmPw}
                      onChange={(e) => setConfirmPw(e.target.value)}
                      disabled={loading}
                      error={confirmPw.length > 0 && !passwordsMatch}
                      helperText={
                        confirmPw.length > 0 && !passwordsMatch
                          ? 'Passwords do not match'
                          : ''
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon sx={{ color: '#2f4ae1' }} />
                          </InputAdornment>
                        ),
                      }}
                    />

                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      fullWidth
                      disabled={!canSubmit}
                      startIcon={
                        loading ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <KeyIcon />
                        )
                      }
                      sx={{
                        py: 1.75,
                        background:
                          'linear-gradient(135deg, #2f4ae1 0%, #12b59f 100%)',
                        textTransform: 'none',
                        fontSize: '1.05rem',
                        fontWeight: 600,
                        borderRadius: 2,
                        boxShadow: '0 8px 24px rgba(47, 74, 225, 0.35)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          background:
                            'linear-gradient(135deg, #2438b8 0%, #0e9179 100%)',
                          boxShadow: '0 12px 32px rgba(47, 74, 225, 0.45)',
                          transform: 'translateY(-2px)',
                        },
                        '&:disabled': {
                          background: '#94a3b8',
                          boxShadow: 'none',
                        },
                      }}
                    >
                      {loading ? 'Saving...' : 'Save Password & Continue'}
                    </Button>
                  </Stack>
                </form>
              </CardContent>
            </Card>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default ForcePasswordChange;
